import { useEffect, useMemo, useRef, useState } from "react";
import { Clock } from "lucide-react";

// An analog clock face for picking a shift time (§12 `shift.startTime` /
// `shift.endTime`, both local `HH:mm` in 24h).
//
// Why a clock rather than <input type="time">: a shift is a span, and people
// reason about it as one — "nine to six" is a shape on a dial, not two
// unrelated numbers. Dragging the hand also makes an overnight shift (end
// before start) obvious, which is easy to typo past in a text field.
//
// The value is always a 24h "HH:mm" string, so the API contract is unchanged.

const CLOCK_SIZE = 208;
const R = CLOCK_SIZE / 2;
const HAND_R = R - 30;

function parse(value) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value || "");
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return { h, m: min };
}

const pad = (n) => String(n).padStart(2, "0");
const to24 = (h12, isPm) => (h12 % 12) + (isPm ? 12 : 0);

/** Angle in degrees (12 o'clock = 0, clockwise) -> nearest step on the dial. */
function angleFor(x, y) {
  const deg = (Math.atan2(x, -y) * 180) / Math.PI;
  return (deg + 360) % 360;
}

export default function ClockTimePicker({ value, onChange, label, id }) {
  const parsed = parse(value);
  const [open, setOpen] = useState(false);
  // Which hand is being set. Picking an hour advances to minutes, the way a
  // physical clock-setting flow works.
  const [mode, setMode] = useState("hour");
  const dialRef = useRef(null);
  const popRef = useRef(null);
  const dragging = useRef(false);

  const hours24 = parsed?.h ?? 9;
  const minutes = parsed?.m ?? 0;
  const isPm = hours24 >= 12;
  const hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12;

  // Close on outside click / Escape, like any other popover in the panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function commit(h24, m) {
    onChange(`${pad(h24)}:${pad(m)}`);
  }

  function pointFromEvent(e) {
    const rect = dialRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const point = e.touches?.[0] ?? e;
    return { x: point.clientX - cx, y: point.clientY - cy };
  }

  function applyFromPointer(e) {
    if (!dialRef.current) return;
    const { x, y } = pointFromEvent(e);
    const deg = angleFor(x, y);
    if (mode === "hour") {
      // 12 positions, 30° apart. The inner ring is unused: AM/PM is an
      // explicit toggle instead, which is far less error-prone than a
      // two-ring 24h dial.
      const idx = Math.round(deg / 30) % 12;
      const picked12 = idx === 0 ? 12 : idx;
      commit(to24(picked12, isPm), minutes);
    } else {
      // Snap to 5-minute steps — shift boundaries are never at :07.
      const step = Math.round(deg / 6) % 60;
      commit(hours24, Math.round(step / 5) * 5 % 60);
    }
  }

  function onPointerDown(e) {
    dragging.current = true;
    applyFromPointer(e);
  }
  function onPointerMove(e) {
    if (!dragging.current) return;
    e.preventDefault();
    applyFromPointer(e);
  }
  function onPointerUp() {
    if (!dragging.current) return;
    dragging.current = false;
    // Setting the hour naturally leads to setting the minutes.
    if (mode === "hour") setMode("minute");
  }

  // Hand angle for whichever unit is being edited.
  const handDeg = mode === "hour" ? (hour12 % 12) * 30 : minutes * 6;
  const handRad = ((handDeg - 90) * Math.PI) / 180;
  const handX = R + HAND_R * Math.cos(handRad);
  const handY = R + HAND_R * Math.sin(handRad);

  const ticks = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const n = i === 0 ? 12 : i;
        const rad = ((i * 30 - 90) * Math.PI) / 180;
        return { n, x: R + HAND_R * Math.cos(rad), y: R + HAND_R * Math.sin(rad) };
      }),
    []
  );

  const display = parsed
    ? `${pad(hour12)}:${pad(minutes)} ${isPm ? "PM" : "AM"}`
    : "Not set";

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        id={id}
        onClick={() => {
          setMode("hour");
          setOpen((o) => !o);
        }}
        className={`flex w-full items-center justify-between rounded border px-3.5 py-2.5 text-left text-sm transition ${
          open
            ? "border-emerald-500 shadow-glow"
            : "border-outline-variant hover:border-emerald-300"
        } bg-surface-container-lowest`}
      >
        <span className={parsed ? "font-medium tabular-nums text-on-surface" : "text-gray-400"}>
          {display}
        </span>
        <Clock size={15} className="shrink-0 text-emerald-600" />
      </button>

      {open && (
        <div className="absolute left-0 z-50 mt-2 w-[248px] rounded-lg border border-emerald-100 bg-white p-3 shadow-level3">
          {/* Digital readout doubles as the hour/minute selector */}
          <div className="mb-3 flex items-center justify-center gap-1">
            <button
              type="button"
              onClick={() => setMode("hour")}
              className={`rounded px-2 py-1 text-2xl font-semibold tabular-nums transition ${
                mode === "hour" ? "bg-emerald-100 text-emerald-800" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {pad(hour12)}
            </button>
            <span className="text-2xl font-semibold text-gray-400">:</span>
            <button
              type="button"
              onClick={() => setMode("minute")}
              className={`rounded px-2 py-1 text-2xl font-semibold tabular-nums transition ${
                mode === "minute" ? "bg-emerald-100 text-emerald-800" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {pad(minutes)}
            </button>
            <div className="ml-2 flex flex-col gap-0.5">
              {["AM", "PM"].map((ap) => {
                const active = (ap === "PM") === isPm;
                return (
                  <button
                    key={ap}
                    type="button"
                    onClick={() => commit(to24(hour12, ap === "PM"), minutes)}
                    className={`rounded px-1.5 py-0.5 text-[10px] font-bold transition ${
                      active ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    {ap}
                  </button>
                );
              })}
            </div>
          </div>

          {/* The dial */}
          <svg
            ref={dialRef}
            width={CLOCK_SIZE}
            height={CLOCK_SIZE}
            className="mx-auto cursor-pointer touch-none select-none"
            onMouseDown={onPointerDown}
            onMouseMove={onPointerMove}
            onMouseUp={onPointerUp}
            onMouseLeave={onPointerUp}
            onTouchStart={onPointerDown}
            onTouchMove={onPointerMove}
            onTouchEnd={onPointerUp}
          >
            <circle cx={R} cy={R} r={R - 2} className="fill-emerald-50/70 stroke-emerald-100" strokeWidth="2" />
            {/* hand */}
            <line x1={R} y1={R} x2={handX} y2={handY} className="stroke-emerald-500" strokeWidth="2" />
            <circle cx={handX} cy={handY} r="16" className="fill-emerald-500" />
            <circle cx={R} cy={R} r="3" className="fill-emerald-600" />

            {mode === "hour"
              ? ticks.map((t) => (
                  <text
                    key={t.n}
                    x={t.x}
                    y={t.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className={`pointer-events-none text-[13px] font-semibold ${
                      t.n === hour12 ? "fill-white" : "fill-gray-600"
                    }`}
                  >
                    {t.n}
                  </text>
                ))
              : ticks.map((t, i) => {
                  const m = i * 5;
                  return (
                    <text
                      key={m}
                      x={t.x}
                      y={t.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className={`pointer-events-none text-[13px] font-semibold ${
                        m === minutes ? "fill-white" : "fill-gray-600"
                      }`}
                    >
                      {pad(m)}
                    </text>
                  );
                })}
          </svg>

          <div className="mt-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100"
            >
              Clear
            </button>
            <p className="text-[10px] text-gray-400">
              {mode === "hour" ? "Pick the hour" : "Pick the minutes"}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded bg-emerald-500 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-600"
            >
              Done
            </button>
          </div>
          {label && <p className="mt-1 text-center text-[10px] text-gray-400">{label}</p>}
        </div>
      )}
    </div>
  );
}
